import React, { useEffect, useState, createContext, useContext } from 'react';
import { RotatingFunction } from './RotatingFunction';
import { RotatingStack } from './RotatingStack';
import { RotatingPlatform } from './RotatingPlatform';
type CopyMode = 'current' | 'outcome' | 'founder';
interface CopyRegistry {
  introSplash: {
    phrase: string;
  };
  marketingHero: {
    eyebrow: string;
    headline: React.ReactNode;
  };
  aiCoworkers: {
    eyebrow: string;
    headline: React.ReactNode;
  };
  howItWorksConnections: {
    headline1: React.ReactNode;
    headline2: string;
  };
  livesInSlack: {
    headline: React.ReactNode;
  };
  computerUse: {
    headline: string;
  };
  quickCapture: {
    headline: string;
  };
  narrativeSection: {
    quote: React.ReactNode;
  };
  productDemo: {
    headline: string;
  };
  wallOfLove: {
    headline: string;
  };
  pricingSection: {
    headline: string;
  };
  closingCta: {
    headline: React.ReactNode;
  };
}
const HOME_COPY: Record<CopyMode, CopyRegistry> = {
  current: {
    introSplash: {
      phrase: 'For the next era of work.'
    },
    marketingHero: {
      eyebrow: 'AI Co-Pilot for Teams',
      headline: <>
          Everyone's new <br className="hidden md:block" data-id="element-1872" data-mp="1fage2x3jsfgw" />
          favorite <span className="text-[#EA580C]" data-id="element-1873" data-mp="1f9wxbjpmvx4g">colleague</span>
        </>
    },
    aiCoworkers: {
      eyebrow: 'Action, not answers',
      headline: <>
          The fastest moving teams run on{' '}
          <span className="text-[#EA580C]" data-id="element-1874" data-mp="16875wdvr4y6o">Giga.</span>
        </>
    },
    howItWorksConnections: {
      headline1: <>
          Giga connects <RotatingStack data-id="element-1875" data-mp="wbkmzumzkh7a" /> and your team in{' '}
          <span className="whitespace-nowrap" data-id="element-1876" data-mp="wcvr5vkcfqyx">
            <RotatingPlatform data-id="element-1877" data-mp="11a5oryhvz8xu" />
          </span>
        </>,
      headline2: 'Giga plugs into your world'
    },
    livesInSlack: {
      headline: '@mention Giga in any channel.'
    },
    computerUse: {
      headline: 'Giga brings its own computer.'
    },
    quickCapture: {
      headline: 'Summon Giga over any app.'
    },
    narrativeSection: {
      quote: <>
          “We're betting the next decade of work belongs to teams who{' '}
          <span className="inline-block bg-[#F6E2D4] rounded-[2rem] px-4 py-1 md:px-6 md:py-2 -mx-1 md:-mx-2 mt-2 md:mt-0 transition-colors hover:bg-[#f2d5c1]" data-id="element-1878" data-mp="2p6ll3jzorbjk">
            automate the busywork
          </span>{' '}
          and{' '}
          <span className="inline-block bg-[#F6E2D4] rounded-[2rem] px-4 py-1 md:px-6 md:py-2 -mx-1 md:-mx-2 mt-2 md:mt-0 transition-colors hover:bg-[#f2d5c1]" data-id="element-1879" data-mp="2fcd7e2fbdn5g">
            pour their energy into what matters.”
          </span>
        </>
    },
    productDemo: {
      headline: 'One adaptive workspace. All of your work.'
    },
    wallOfLove: {
      headline: "Teams can't stop talking about Giga."
    },
    pricingSection: {
      headline: 'Simple plans that scale with your work.'
    },
    closingCta: {
      headline: <>
          Wake up tomorrow
          <br data-id="element-1880" data-mp="3qvz5as4zs7f2" />
          <span className="text-[#EA580C]" data-id="element-1881" data-mp="3qw7eoa1wouli">already ahead.</span>
        </>
    }
  },
  outcome: {
    introSplash: {
      phrase: 'Reclaim your time.'
    },
    marketingHero: {
      eyebrow: 'AI Co-Pilot for Teams',
      headline: <>
          Get hours back <br className="hidden md:block" data-id="element-1882" data-mp="c173645oy3ej" />
          every single <span className="text-[#EA580C]" data-id="element-1883" data-mp="c3taq3sl0iwr">day</span>
        </>
    },
    aiCoworkers: {
      eyebrow: 'Action, not answers',
      headline: <>
          Your whole to-do list,{' '}
          <span className="text-[#EA580C]" data-id="element-1884" data-mp="lxjy15cz2nav">done by tonight.</span>
        </>
    },
    howItWorksConnections: {
      headline1: 'Never switch tabs again.',
      headline2: 'Connect everything you already use'
    },
    livesInSlack: {
      headline: 'Answers where you already are.'
    },
    computerUse: {
      headline: 'Move faster, or get left behind.'
    },
    quickCapture: {
      headline: 'Capture ideas before they disappear.'
    },
    narrativeSection: {
      quote: <>
          “Imagine what your team could build if they never had to{' '}
          <span className="inline-block bg-[#F6E2D4] rounded-[2rem] px-4 py-1 md:px-6 md:py-2 -mx-1 md:-mx-2 mt-2 md:mt-0 transition-colors hover:bg-[#f2d5c1]" data-id="element-1885" data-mp="26fkhjaqwpbnc">
            copy-paste data
          </span>{' '}
          again.”
        </>
    },
    productDemo: {
      headline: 'Focus on the outcome, not the process.'
    },
    wallOfLove: {
      headline: 'How teams got their weekends back.'
    },
    pricingSection: {
      headline: 'Start free. Scale as you grow.'
    },
    closingCta: {
      headline: <>
          Start doing the work
          <br data-id="element-1886" data-mp="1wig3jy77smhm" />
          <span className="text-[#EA580C]" data-id="element-1887" data-mp="1wixvwjqragk9">that matters.</span>
        </>
    }
  },
  founder: {
    introSplash: {
      phrase: 'Do more with the team you have.'
    },
    marketingHero: {
      eyebrow: 'An AI co-pilot for everything. Seriously.',
      headline: <>
          Meet Giga, your <br className="hidden md:block" data-id="element-1888" data-mp="33w9p7l2jf84n" />
          AI co-pilot for <RotatingFunction data-id="element-1889" data-mp="33vq7a4cw0jqm" />
        </>
    },
    aiCoworkers: {
      eyebrow: 'A team in a box',
      headline: <>
          The work across every function —{' '}
          <span className="text-[#EA580C]" data-id="element-1890" data-mp="3gfn4kaebsj5o">handled for you.</span>
        </>
    },
    howItWorksConnections: {
      headline1: <>
          Giga connects <RotatingStack data-id="element-1891" data-mp="31jpdh6j9e50b" /> and your team in{' '}
          <span className="whitespace-nowrap" data-id="element-1892" data-mp="36hh5s1ntft51">
            <RotatingPlatform data-id="element-1893" data-mp="36i925ep8luwd" />
          </span>
        </>,
      headline2: 'Giga plugs into your world'
    },
    livesInSlack: {
      headline: '@mention Giga in any channel.'
    },
    computerUse: {
      headline: 'Your whole back office, handled.'
    },
    quickCapture: {
      headline: 'Catch every idea before it slips.'
    },
    narrativeSection: {
      quote: <>
          “A lean team plus Giga can outwork companies{' '}
          <span className="inline-block bg-[#F6E2D4] rounded-[2rem] px-4 py-1 md:px-6 md:py-2 -mx-1 md:-mx-2 mt-2 md:mt-0 transition-colors hover:bg-[#f2d5c1]" data-id="element-1894" data-mp="gz8bem4v724w">
            ten times their size.”
          </span>
        </>
    },
    productDemo: {
      headline: 'Run every part of the business — without hiring for it.'
    },
    wallOfLove: {
      headline: 'How lean teams punch above their weight.'
    },
    pricingSection: {
      headline: 'Start free. Grow when you do.'
    },
    closingCta: {
      headline: <>
          Grow your business,
          <br data-id="element-1895" data-mp="hoqyrc8weev1" />
          <span className="text-[#EA580C]" data-id="element-1896" data-mp="cn1k7mmgvj27">not your headcount.</span>
        </>
    }
  }
};
interface CopyModeContextType {
  mode: CopyMode;
  setMode: (mode: CopyMode) => void;
  toggle: () => void;
}
const CopyModeContext = createContext<CopyModeContextType | undefined>(undefined);
export function CopyModeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [mode, setModeState] = useState<CopyMode>('current');
  useEffect(() => {
    const saved = localStorage.getItem('giga_copy_mode') as CopyMode;
    if (saved === 'current' || saved === 'outcome' || saved === 'founder') {
      setModeState(saved);
    }
  }, []);
  const setMode = (newMode: CopyMode) => {
    setModeState(newMode);
    localStorage.setItem('giga_copy_mode', newMode);
  };
  const toggle = () => {
    setMode(mode === 'current' ? 'outcome' : mode === 'outcome' ? 'founder' : 'current');
  };
  return <CopyModeContext.Provider value={{
    mode,
    setMode,
    toggle
  }} data-id="element-1897" data-mp="2ekvbxlvtn17h">
      
      {children}
    </CopyModeContext.Provider>;
}
export function useCopyMode() {
  const context = useContext(CopyModeContext);
  if (!context) {
    throw new Error('useCopyMode must be used within a CopyModeProvider');
  }
  return context;
}
export function useCopy() {
  const {
    mode
  } = useCopyMode();
  return HOME_COPY[mode];
}
export function CopyModeToggle() {
  const {
    mode,
    toggle
  } = useCopyMode();
  return <button onClick={toggle} className="fixed bottom-4 left-4 z-[9999] bg-black text-white px-3 py-1.5 rounded-full text-xs font-mono font-bold shadow-lg hover:bg-neutral-800 transition-colors flex items-center gap-2 border border-white/20" data-id="element-1898" data-mp="z4wgnb7ayzon">
      
      <span className="w-2 h-2 rounded-full bg-[#EA580C] animate-pulse" data-id="element-1899" data-mp="3spunpcglp1io" />
      COPY: {mode.toUpperCase()}
    </button>;
}